import { layersFormattedAtom } from '@app/domains/layers/state/layers.atoms'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

export type HazardGroupBucket = Record<string, string[]>

const DEFAULT_HAZARD_GROUP = 'Ungrouped hazard'

const scenarioMatchesHazardId = (scenario: unknown, hazardId: string): boolean => {
  if (typeof scenario === 'string') return scenario === hazardId
  if (scenario === null || typeof scenario !== 'object') return false

  const candidateValues = [
    'assetTag' in scenario ? scenario.assetTag : undefined,
    'key' in scenario ? scenario.key : undefined,
    'value' in scenario ? scenario.value : undefined,
    'id' in scenario ? scenario.id : undefined,
  ]

  return candidateValues.some((value) => value === hazardId)
}

const getScenarioHazardTitle = (scenario: unknown): string | null => {
  if (scenario === null || typeof scenario !== 'object') return null

  const hazardTitle = 'hazard_title' in scenario ? scenario.hazard_title : undefined
  const hazardName = 'hazard_name' in scenario ? scenario.hazard_name : undefined
  const displayName = 'display_name' in scenario ? scenario.display_name : undefined

  if (typeof hazardTitle === 'string' && hazardTitle.trim() !== '') return hazardTitle
  if (typeof hazardName === 'string' && hazardName.trim() !== '') return hazardName
  if (typeof displayName === 'string' && displayName.trim() !== '') return displayName

  return null
}

const getHazardGroup = (hazardDetails: unknown, hazardId: string): string => {
  if (!Array.isArray(hazardDetails)) return DEFAULT_HAZARD_GROUP

  const matchedScenario = hazardDetails
    .flatMap((detail) => {
      if (detail === null || typeof detail !== 'object') return []

      const scenarios = 'scenarios' in detail ? detail.scenarios : undefined

      return Array.isArray(scenarios) ? scenarios.map((scenario) => ({ detail, scenario })) : []
    })
    .find(({ scenario }) => scenarioMatchesHazardId(scenario, hazardId))

  if (matchedScenario === undefined) return DEFAULT_HAZARD_GROUP

  const scenarioTitle = getScenarioHazardTitle(matchedScenario.scenario)
  if (scenarioTitle !== null) return scenarioTitle

  const detail = matchedScenario.detail
  const title = 'title' in detail ? detail.title : undefined
  const type = 'type' in detail ? detail.type : undefined

  if (typeof title === 'string' && title.trim() !== '') return title
  if (typeof type === 'string' && type.trim() !== '') return type

  return DEFAULT_HAZARD_GROUP
}

export const useHazardGroupBuckets = (hazardIds: string[]): HazardGroupBucket[] => {
  const layers = useAtomValue(layersFormattedAtom)

  return useMemo(() => {
    const bucketsByGroup = new Map<string, string[]>()

    for (const hazardId of hazardIds) {
      const group = getHazardGroup(layers.hazards.hazardDetails, hazardId)
      const currentBucket = bucketsByGroup.get(group) ?? []

      bucketsByGroup.set(group, [...currentBucket, hazardId])
    }

    return Array.from(bucketsByGroup.entries()).map(([group, groupedHazardIds]) => ({
      [group]: groupedHazardIds,
    }))
  }, [hazardIds, layers.hazards.hazardDetails])
}
